import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_AUDIT_LOGS } from '../graphql/admin';
import { AdminLayout } from '../components/AdminLayout';

export function AdminAuditLogPage() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [filters, setFilters] = useState({
    adminEmail: '',
    action: '',
    resourceType: '',
    startDate: '',
    endDate: '',
    successOnly: '',
  });

  const { data, loading, refetch } = useQuery(GET_AUDIT_LOGS, {
    variables: {
      filters: {
        page,
        pageSize,
        adminEmail: filters.adminEmail || undefined,
        action: filters.action || undefined,
        resourceType: filters.resourceType || undefined,
        startDate: filters.startDate ? new Date(filters.startDate).toISOString() : undefined,
        endDate: filters.endDate ? new Date(filters.endDate).toISOString() : undefined,
        successOnly: filters.successOnly === 'success' ? true : filters.successOnly === 'error' ? false : undefined,
      },
    },
  });

  const logs = data?.adminAuditLogs?.logs || [];
  const total = data?.adminAuditLogs?.total || 0;
  const totalPages = data?.adminAuditLogs?.totalPages || 1;

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const handleClearFilters = () => {
    setFilters({
      adminEmail: '',
      action: '',
      resourceType: '',
      startDate: '',
      endDate: '',
      successOnly: '',
    });
    setPage(1);
    refetch();
  };

  // Extract unique actions and resource types for filters
  const uniqueActions = Array.from(new Set(logs.map((log: any) => log.action)));
  const uniqueResourceTypes = Array.from(new Set(logs.map((log: any) => log.resourceType)));

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-600 mt-1">
            Complete history of all admin actions ({total.toLocaleString()} entries)
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Admin Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
              <input
                type="text"
                value={filters.adminEmail}
                onChange={(e) => setFilters({ ...filters, adminEmail: e.target.value })}
                placeholder="admin@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Action */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <input
                type="text"
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                placeholder="create_user, update_plan, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Resource Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resource Type
              </label>
              <input
                type="text"
                value={filters.resourceType}
                onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })}
                placeholder="user, organization, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Success/Error */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.successOnly}
                onChange={(e) => setFilters({ ...filters, successOnly: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="success">Success Only</option>
                <option value="error">Errors Only</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => {
                setPage(1);
                refetch();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Apply Filters
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Clear All
            </button>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Audit Log Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse">Loading audit logs...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No audit logs found matching your criteria
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Timestamp
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Admin
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Action
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Resource
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        IP Address
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log: any) => (
                      <tr
                        key={log.id}
                        className={`border-b hover:bg-gray-50 ${
                          !log.success ? 'bg-red-50' : ''
                        }`}
                      >
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDateTime(log.createdAt)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{log.adminEmail}</div>
                            {log.adminUserId && (
                              <div className="text-xs text-gray-500">ID: {log.adminUserId}</div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            {log.action}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="text-gray-900">{log.resourceType}</div>
                          {log.resourceId && (
                            <div className="text-xs text-gray-500 font-mono">{log.resourceId}</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {log.success ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                              Success
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">
                              Error
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 font-mono">
                          {log.ipAddress || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => {
                              const details = {
                                id: log.id,
                                adminEmail: log.adminEmail,
                                action: log.action,
                                resourceType: log.resourceType,
                                resourceId: log.resourceId,
                                changes: log.changes,
                                metadata: log.metadata,
                                errorMessage: log.errorMessage,
                                ipAddress: log.ipAddress,
                                userAgent: log.userAgent,
                                createdAt: log.createdAt,
                              };
                              alert(
                                'Audit Log Details:\n\n' +
                                  JSON.stringify(details, null, 2) +
                                  '\n\nNote: In production, this should open a modal with formatted details.',
                              );
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t">
                <div className="text-sm text-gray-700">
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of{' '}
                  {total} logs
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`px-3 py-1 rounded-md text-sm ${
                            page === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Export Note */}
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Audit logs are immutable and stored permanently. For compliance
            purposes, consider implementing export functionality to CSV/JSON for long-term
            archival.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
